/**
 * Coverage of the tronc commun référentiel by the year's lessons.
 *
 * The unit of progression is the *contenu d'apprentissage* — the heading a
 * block of attendus hangs from in the référentiel, which is what a teacher
 * plans a lesson around. P3 has 86 of them in français, 64 in mathématiques,
 * 9 in éveil géographique; 100 % means every one of them has been worked on at
 * least once during the year.
 *
 * Attendus are counted too, but only as detail: tagging one attendu of a
 * contenu already means the class met that content, and demanding all of them
 * would make the bar unreachable in practice.
 */
import type { Level } from './levels.ts';
import { attendusFor, groupAttendus, type Attendu } from './referentiel.ts';
import { CURRICULUM_SUBJECT_NAMES } from './subjects.ts';

/** Anything carrying tagged attendus — a lesson, or a week-template entry. */
export interface CompetenceBearing {
  readonly competenceIds: readonly string[];
}

/** One contenu d'apprentissage, and how much of it the lessons have touched. */
export interface PointProgress {
  section: string;
  parent: string;
  attendus: readonly Attendu[];
  /** Attendus of this contenu tagged on at least one lesson. */
  coveredAttendus: number;
  /** The contenu counts as worked on as soon as one of its attendus is. */
  done: boolean;
}

export interface SubjectProgress {
  subject: string;
  points: readonly PointProgress[];
  totalPoints: number;
  donePoints: number;
  totalAttendus: number;
  coveredAttendus: number;
  /** 0-100, and only ever 100 when nothing is left. */
  percent: number;
}

export interface Progression {
  level: Level;
  /** Every subject, in curriculum order — including those outside the tronc
   *  commun, which come back with nothing so the page can say so. */
  subjects: readonly SubjectProgress[];
  totalPoints: number;
  donePoints: number;
  totalAttendus: number;
  coveredAttendus: number;
  percent: number;
}

/**
 * Round without ever lying at the ends: a single contenu left must not read
 * 100 %, and a single one done must not read 0 %.
 */
export function percentOf(done: number, total: number): number {
  if (total <= 0 || done <= 0) return 0;
  if (done >= total) return 100;
  return Math.min(99, Math.max(1, Math.round((done / total) * 100)));
}

/**
 * The attendus tagged anywhere in the given rows.
 *
 * Ids are matched against the référentiel by the caller, so an id left over
 * from a lesson whose subject changed still counts for the subject it actually
 * belongs to, and an id from a dataset update that dropped it counts for none.
 */
export function coveredAttenduIds(rows: readonly CompetenceBearing[]): Set<string> {
  const ids = new Set<string>();
  for (const row of rows) {
    for (const id of row.competenceIds) ids.add(id);
  }
  return ids;
}

export function subjectProgress(
  subject: string,
  level: Level,
  covered: ReadonlySet<string>,
): SubjectProgress {
  const points: PointProgress[] = [];
  let donePoints = 0;
  let totalAttendus = 0;
  let coveredAttendus = 0;

  for (const group of groupAttendus(attendusFor(subject, level))) {
    const hits = group.attendus.filter((a) => covered.has(a.id)).length;
    if (hits > 0) donePoints += 1;
    totalAttendus += group.attendus.length;
    coveredAttendus += hits;
    points.push({
      section: group.section,
      parent: group.parent,
      attendus: group.attendus,
      coveredAttendus: hits,
      done: hits > 0,
    });
  }

  return {
    subject,
    points,
    totalPoints: points.length,
    donePoints,
    totalAttendus,
    coveredAttendus,
    percent: percentOf(donePoints, points.length),
  };
}

/**
 * Progression across every subject for the class's year.
 *
 * Returns zeroes rather than throwing on a year with no référentiel extracted
 * yet, so the page shows an empty state instead of breaking.
 */
export function progressionForLevel(
  level: Level,
  rows: readonly CompetenceBearing[],
): Progression {
  const covered = coveredAttenduIds(rows);
  const subjects = CURRICULUM_SUBJECT_NAMES.map((subject) => subjectProgress(subject, level, covered));

  const sum = (pick: (s: SubjectProgress) => number): number =>
    subjects.reduce((total, s) => total + pick(s), 0);

  const totalPoints = sum((s) => s.totalPoints);
  const donePoints = sum((s) => s.donePoints);

  return {
    level,
    subjects,
    totalPoints,
    donePoints,
    totalAttendus: sum((s) => s.totalAttendus),
    coveredAttendus: sum((s) => s.coveredAttendus),
    percent: percentOf(donePoints, totalPoints),
  };
}
